import React, { FormEvent, useEffect, useState } from 'react';

import CaptchaField from 'common/components/CaptchaField';
import { ErrorMessages } from 'common/components/Form/ErrorMessages';
import { WithFormStateProps } from 'common/components/Form/withFormState';
import Input from 'common/components/Input';
import loadReCaptcha, {
  getReCaptchaResponse,
  injectReCaptcha
} from 'common/third_party/captcha';
import { path } from 'common/tools/network/routing';
import trans from 'common/tools/translations/trans';
import { isString } from 'common/types';

export type SubmissionProps = {
  captchaToken: string;
  email: string;
  password: string;
};

export type SignFormProps = {
  displayTermsPolicy?: boolean;
  errorMessages: string[];
  onSubmit: (values: SubmissionProps) => void;
  placeholderEmail?: string;
  placeholderPassword?: string;
  recoverPasswordUrl?: string;
  submitButtonLabel: string;
  submitting: boolean;
};

type SignFormFields = 'email' | 'password';

const captchaErrorMessage = trans('userspace.sign-up.captcha-error');
const captchaErrorSystem = trans('userspace.page-error.description');

const SignForm = ({
  displayTermsPolicy,
  errorMessages,
  fieldsState,
  formErrors,
  formValues,
  isFormValidating,
  onFieldBlur,
  onFieldChange,
  onSubmit,
  placeholderEmail,
  placeholderPassword,
  recoverPasswordUrl,
  submitButtonLabel,
  submitting,
  validateForm
}: WithFormStateProps<SignFormFields> & SignFormProps) => {
  // https://developers.google.com/recaptcha/docs/display
  // get the DOM anchor for Google reCaptcha
  const reCaptchaRef = React.createRef<HTMLDivElement>();
  // the Google reCaptcha widget id
  const [widgetId, setWidgetId] = useState<number>();

  const [annotationsHover, setAnnotationsHover] = useState(false);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(captchaErrorMessage);

  const [email, setEmail] = useState(formValues.email);
  const [password, setPassword] = useState(formValues.password);

  useEffect(() => onFieldChange('email', email), [email]);
  useEffect(() => onFieldChange('password', password), [password]);

  useEffect(() => {
    const anchor = reCaptchaRef.current;
    if (!anchor) return;
    loadReCaptcha(() => {
      setWidgetId(injectReCaptcha(anchor));
    });
  }, []);

  const handleBlur = (field: SignFormFields) => {
    // Validation may "push down" the DOM which will "abort" user click on CGU links
    if (annotationsHover) return;
    onFieldBlur(field);
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();

    if (isValid) {
      const gRecaptchaResponse = await getReCaptchaResponse(widgetId).catch(
        err => err
      );

      setShowCaptchaError(
        !gRecaptchaResponse || gRecaptchaResponse === 'error'
      );
      setErrorMessage(
        gRecaptchaResponse === 'error'
          ? captchaErrorSystem
          : captchaErrorMessage
      );

      if (
        gRecaptchaResponse &&
        isString(formValues.email) &&
        isString(formValues.password)
      ) {
        onSubmit({
          captchaToken: gRecaptchaResponse,
          email: formValues.email,
          password: formValues.password
        });
      }
    }
  };

  const loading = isFormValidating || submitting;

  const termsAndPolicy = trans('userspace.sign-up.terms', {
    client: path('gdpr_client'),
    confidentiality: path('privacy_policy'),
    user: path('cgu')
  });

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="secure-separator" data-txt={trans('account.or')} />
      <Input
        className="form-input text-input"
        containerClassName="form-fieldset"
        errorClassName="input-error-message"
        errors={formErrors.email}
        name="email"
        onBlur={() => handleBlur('email')}
        onChange={e => setEmail(e.target.value)}
        placeholder={placeholderEmail}
        type="email"
        validated={fieldsState.email?.validated}
        validating={fieldsState.email?.validating}
        value={email}
      />
      <Input
        className="form-input text-input"
        containerClassName="form-fieldset"
        errorClassName="input-error-message"
        errors={formErrors.password}
        helpText={trans('userspace.sign-up.hint.password')}
        name="password"
        onBlur={() => handleBlur('password')}
        onChange={e => setPassword(e.target.value)}
        placeholder={placeholderPassword}
        type="password"
        validated={fieldsState.password?.validated}
        validating={fieldsState.password?.validating}
        value={password}
      />
      <ErrorMessages className="input-error-message" messages={errorMessages} />
      {recoverPasswordUrl && (
        <a className="help-text" href={recoverPasswordUrl}>
          {trans('userspace.sign-in.forgot_password')}
        </a>
      )}
      <CaptchaField
        ref={reCaptchaRef}
        error={showCaptchaError}
        errorMessage={errorMessage}
      />
      {loading ? (
        <div className="button button-loading">...</div>
      ) : (
        <button
          type="submit"
          disabled={submitting}
          className="form-input button button-md button-inverse-full submit-button vertical-spacing"
        >
          {submitButtonLabel}
        </button>
      )}

      {displayTermsPolicy && (
        <div
          className="help-text"
          onMouseEnter={() => {
            setAnnotationsHover(true);
          }}
          onMouseLeave={() => {
            setAnnotationsHover(false);
          }}
          dangerouslySetInnerHTML={{ __html: termsAndPolicy }}
        />
      )}
    </form>
  );
};

export default SignForm;
